import type { PaginationQuery } from '../../../types/pagination';
import { appendPaginationQuery } from '../../../types/pagination';
import type { ApiClient } from '../client';
import type { Uuid } from '../types/common';
import type {
  ApproveExpenseRequest,
  AttachExpensePicRequest,
  ExpenseCategory,
  ExpenseGroupBy,
  ExpenseGroupReportResponse,
  ExpenseReportPeriod,
  ExpenseReportResponse,
  ListExpenseCategoriesResponse,
  ListExpensesResponse,
  RecordExpenseRequest,
} from '../types/expenses';

export function bindExpenses(client: ApiClient) {
  return {
    async listExpenseCategories(params?: PaginationQuery) {
      const qs = new URLSearchParams();
      appendPaginationQuery(qs, params);
      const query = qs.toString();
      return client.request<ListExpenseCategoriesResponse>(
        query ? `/expenses/categories?${query}` : '/expenses/categories'
      );
    },

    async createExpenseCategory(body: { name: string; description?: string | null }) {
      return client.request<{ category_id: Uuid }>('/expenses/categories', {
        method: 'POST',
        body: JSON.stringify(body),
      });
    },

    async updateExpenseCategory(
      categoryId: string,
      body: { name: string; description?: string | null }
    ) {
      return client.request<ExpenseCategory>(
        `/expenses/categories/${encodeURIComponent(categoryId)}`,
        {
          method: 'PUT',
          body: JSON.stringify(body),
        }
      );
    },

    async deleteExpenseCategory(categoryId: string) {
      return client.request<void>(`/expenses/categories/${encodeURIComponent(categoryId)}`, {
        method: 'DELETE',
      });
    },

    async listExpenses(
      params?: PaginationQuery & {
        from?: string;
        to?: string;
        category_id?: string;
        status?: string;
      }
    ) {
      const qs = new URLSearchParams();
      appendPaginationQuery(qs, params);
      if (params?.from?.trim()) qs.set('from', params.from.trim());
      if (params?.to?.trim()) qs.set('to', params.to.trim());
      if (params?.category_id?.trim()) qs.set('category_id', params.category_id.trim());
      if (params?.status?.trim()) qs.set('status', params.status.trim());
      const query = qs.toString();
      return client.request<ListExpensesResponse>(query ? `/expenses?${query}` : '/expenses');
    },

    async recordExpense(body: RecordExpenseRequest) {
      return client.request<{ expense_id: Uuid }>('/expenses', {
        method: 'POST',
        body: JSON.stringify(body),
      });
    },

    async attachExpensePic(body: AttachExpensePicRequest) {
      return client.request<void>('/expenses/pic', {
        method: 'POST',
        body: JSON.stringify(body),
      });
    },

    async approveExpense(body: ApproveExpenseRequest) {
      return client.request<void>('/expenses/approve', {
        method: 'POST',
        body: JSON.stringify(body),
      });
    },

    async getExpenseReport(params?: {
      period?: ExpenseReportPeriod;
      from?: string;
      to?: string;
      category_id?: string;
    }) {
      const qs = new URLSearchParams();
      if (params?.period) qs.set('period', params.period);
      if (params?.from?.trim()) qs.set('from', params.from.trim());
      if (params?.to?.trim()) qs.set('to', params.to.trim());
      if (params?.category_id?.trim()) qs.set('category_id', params.category_id.trim());
      const query = qs.toString();
      return client.request<ExpenseReportResponse>(
        query ? `/expenses/report?${query}` : '/expenses/report'
      );
    },

    async getExpenseGroupReport(params?: {
      group_by?: ExpenseGroupBy;
      from?: string;
      to?: string;
    }) {
      const qs = new URLSearchParams();
      if (params?.group_by) qs.set('group_by', params.group_by);
      if (params?.from?.trim()) qs.set('from', params.from.trim());
      if (params?.to?.trim()) qs.set('to', params.to.trim());
      const query = qs.toString();
      return client.request<ExpenseGroupReportResponse>(
        query ? `/expenses/report/group?${query}` : '/expenses/report/group'
      );
    },

    async downloadExpensePdf(expenseId: string): Promise<Blob> {
      const path = `/expenses/${encodeURIComponent(expenseId)}/render/pdf`;
      const url = `${client.baseUrl}${path}`;
      const response = await fetch(url, {
        method: 'GET',
        headers: client.authHeaders(),
      });
      if (!response.ok) {
        throw new Error(`Expense PDF download failed: ${response.status}`);
      }
      return response.blob();
    },
  };
}
